import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/authApi';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { QK } from './queryKeys';
import type { LoginRequest, UpdateProfileRequest } from '@/types';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { disconnectSocket } from '@/lib/socket';
import { getApiErrorMessage } from '@/utils';

/**
 * LOGIN — 2 bước:
 * 1. POST /auth/login  → nhận access_token + refresh_token
 * 2. GET  /users/profile → nhận thông tin user + roles, chuẩn hoá thành User
 */
export function useLogin() {
  const { setTokens, setUser } = useAuthStore();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      // Bước 1: đăng nhập lấy tokens
      const tokens = await authApi.login(data);

      // Lưu tokens tạm vào store (để interceptor gắn vào header bước 2)
      setTokens(tokens.access_token, tokens.refresh_token);

      // Bước 2: lấy profile + roles
      const user = await authApi.getProfile();

      return { tokens, user };
    },
    onSuccess: ({ user }) => {
      setUser(user);
      addToast('success', `Chào mừng, ${user.fullName || user.username}!`);
    },
    onError: (err: any) => {
      // Nếu thất bại thì xoá token đã lưu tạm
      useAuthStore.getState().logout();
      addToast('error', getApiErrorMessage(err, 'Sai thông tin đăng nhập'));
    },
  });
}

/**
 * REGISTER — gọi API đăng ký, sau đó tự đăng nhập để lấy profile
 */
export function useRegister() {
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: async (data: any) => {
      return await authApi.register(data);
    },
    onSuccess: () => {
      addToast('success', 'Tạo tài khoản thành công!');
    },
    onError: (err: any) => {
      addToast('error', getApiErrorMessage(err, 'Đăng ký thất bại. Vui lòng thử lại'));
    },
  });
}

/**
 * GET PROFILE — refetch profile từ server (dùng khi app mount để sync)
 */
export function useProfile() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const updateUser = useAuthStore((s) => s.updateUser);

  const query = useQuery({
    queryKey: QK.ME,
    queryFn: authApi.getProfile,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 10,
  });

  useEffect(() => {
    if (query.data) {
      updateUser(query.data);
    }
  }, [query.data, updateUser]);

  return query;
}

/**
 * UPDATE PROFILE
 */
export function useUpdateProfile() {
  const { updateUser } = useAuthStore();
  const { addToast } = useUIStore();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProfileRequest }) => authApi.updateProfile(id, data),
    onSuccess: (user) => {
      updateUser(user);
      qc.setQueryData(QK.ME, user);
      addToast('success', 'Cập nhật hồ sơ thành công!');
    },
    onError: (err: any) => {
      addToast('error', getApiErrorMessage(err, 'Cập nhật thất bại'));
    },
  });
}

/**
 * LOGOUT — disconnect socket, clear stores, navigate to login
 */
export function useLogout() {
  const { logout } = useAuthStore();
  const { addToast } = useUIStore();
  const qc = useQueryClient();
  const navigate = useNavigate();

  return () => {
    disconnectSocket();
    logout();
    qc.clear();
    addToast('info', 'Đã đăng xuất');
    navigate('/login');
  };
}
